// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import { intersection } from 'lodash';
import store from '../redux/store';

import { isDev, isLocal } from '../config/env';
import { getLDClient } from '../launch-darkly';
import { hideMenuForNetworks, menuConfigForNetworks } from './menuConfig';
import { paths } from './paths';
import { RouteParamType, RouteType } from './types';

const MARKETPLACE_APP_URL = import.meta.env.VITE_WS_MARKETPLACE_APP_URL;
const ZENDESK_APP_URL = import.meta.env.VITE_WS_ZENDESK_APP_URL;

type BuildMarketplaceLinkType = {
  organization: any;
  location?: any;
  channelId?: string;
  path: string;
  params?: Record<string, string>;
  baseUrl?: string;
};
export function buildMarketplaceLink({
  organization,
  location,
  channelId,
  path = '',
  params = {},
  baseUrl,
}: BuildMarketplaceLinkType): string {
  if (!path) return '';

  const root =
    baseUrl ??
    (isLocal()
      ? // only use the default if local
        MARKETPLACE_APP_URL || 'http://lego:4000'
      : MARKETPLACE_APP_URL);

  Object.keys(params).forEach((key) => {
    const value = params[key];
    path = path.replaceAll(`{${key}}`, value);
  });

  const url = (root + path)
    .replaceAll('{marketplaceSchoolId}', location?.marketplaceSchoolId ?? organization.marketplaceSchoolId ?? '')
    .replaceAll('{locationId}', location?.id ?? '')
    .replaceAll('{channelId}', channelId ?? '');

  return encodeURI(url);
}

export function buildHelpCenterLink(path: string): string {
  const root = isLocal()
    ? // only use the default if local
      ZENDESK_APP_URL || 'https://wonderschool.zendesk.com'
    : ZENDESK_APP_URL;

  return root + path;
}

// replace params in the path with the values passed in
// the path should be in the format of /path/{param1}/path/{param2} or
// /path/:param1/path/:param2
export function buildParameterizedPath(route: RouteType, params?: RouteParamType[]): string {
  let path = route.path;
  params?.forEach((param) => {
    path = path.replace(`{${param.name}}`, param.value); // try handlebar style
    path = path.replace(`:${param.name}`, param.value); // try rails style
  });
  return path;
}

// Have to special case the onboarding link because the staging host is not the same as all other MP links
export function buildOnboardingLink(route: RouteType): string {
  const root = isDev() // notice how we are checking for dev here, not local like the function above
    ? 'https://www.wonderschool.io'
    : MARKETPLACE_APP_URL || (isLocal() ? 'http://lego:4000' : '');

  return root + route.path;
}

export function shouldHideForNonFCCs(): boolean {
  const {
    organizations: { currentOrganization },
  } = store.getState() as any;
  return currentOrganization?.programType !== 'familyChildCare';
}
export function shouldHideForFCCs(): boolean {
  const {
    organizations: { currentOrganization },
  } = store.getState() as any;
  return currentOrganization?.programType === 'familyChildCare';
}

// Should Hide from non-directors, but allow moxit admins
export function shouldHideForNonDirectors(): boolean {
  const {
    organizations: { currentOrganization },
    user,
  } = store.getState() as any;
  if (!currentOrganization || !user) return true;
  // Provide access if user is moxit admin
  // (neftaly) Added for troubleshooting on headless pages
  if (user?.claims?.moxitAdmin) return false; // don't hide for moxit admins
  return currentOrganization?.createdBy !== user?.uid; // hide if the created by is not the current user
}

/**
 * Should hide Menu for Networks
 * This is the universal function that can used for other networks too.
 **/
export function shouldHideForNetwork(routeName): boolean {
  // Fetch the network ids for respective environments
  const {
    organizations: { currentOrganization },
  } = store.getState() as any;

  if (!currentOrganization) return true; // default to hide if no current organization

  if (currentOrganization?.networks?.length && hideMenuForNetworks?.length) {
    // if network id is present, and menu is present in the menuConfigForNetworks, show the menu
    // else hide the menu
    const foundNetworkIds = intersection(currentOrganization.networks, hideMenuForNetworks);
    if (foundNetworkIds.length) return !menuConfigForNetworks[foundNetworkIds[0]][routeName];
    else return false;
  }
  //if no network id is present, show all the menus
  return false;
}

/**
 * Interested List destination (MP-7001), mirroring Nova's mirror nav
 * (ws-common#7592). Providers with no network go to the Nova Interested List,
 * the only one carrying Invite to Enroll. Two cases stay on the legacy,
 * school-scoped page:
 *
 *   - Network (SLED) providers, because the Nova page 403s for them (MP-7073).
 *   - A Wonderschool admin impersonating a program, because
 *     `/ccms/interested-list` has no school in its path and resolves the
 *     admin's own listing instead of the impersonated one.
 **/
export function getInterestedListPath(): string {
  const {
    organizations: { currentOrganization },
    user,
  } = store.getState() as any;

  const isInNetwork = !!currentOrganization?.networks?.length;
  // Same rule as useOrganizationImpersonation's isImpersonatingOrganization.
  const isImpersonating =
    !!user?.impersonatedOrganization && user.impersonatedOrganization !== user.defaultOrganization;

  return isInNetwork || isImpersonating ? paths.external.interestedFamilies : paths.external.interestedList;
}

/**
 * Should hide Menu for MP features that haven't been enabled for the Provider
 **/
export function shouldHideIfNotEnabledOnMarketplace(feature: string): boolean {
  // Fetch the network ids for respective environments
  const {
    organizations: { currentOrganization },
    marketplace: { featuresEnabled },
  } = store.getState() as any;

  if (!currentOrganization) return true; // default to hide if no current organization
  if (!currentOrganization.marketplaceSchoolId) return true; // default to hide if no marketplaceSchoolId

  return !featuresEnabled[feature];
}

export function shouldHideFoodProgram(): boolean {
  return shouldHideLaunchDarkly('food-program-enabled');
}
export function shouldHideClaimsManagement(): boolean {
  return shouldHideLaunchDarkly('is-claims-management-enabled');
}
export function shouldHideAppStore(): boolean {
  return shouldHideLaunchDarkly('is-app-store-enabled');
}
export function shouldHideOpenLedger(): boolean {
  return shouldHideLaunchDarkly('is-open-ledger-enabled');
}
export function shouldHideTaxForms(): boolean {
  return shouldHideLaunchDarkly('is-tax-forms-enabled');
}
export function shouldHideUnenrollV2(): boolean {
  return shouldHideLaunchDarkly('is-unenroll-v2-enabled');
}
// Gates the Add Families / Add-to-billing bulk-import flow. Default OFF.
// shouldHideLaunchDarkly reads the raw flag key via ldClient.variation(),
// so this uses the kebab-case key ('add-families-bulk-import'), not the
// camelCased `addFamiliesBulkImport` that useFlags() exposes.
export function shouldHideAddFamilies(): boolean {
  return shouldHideLaunchDarkly('add-families-bulk-import');
}
function shouldHideLaunchDarkly(flag: string): boolean {
  const ldClient = getLDClient();
  const ldEnabled = ldClient?.variation(flag, false);
  return !ldEnabled;
}

export function getPaths() {
  return paths;
}
